import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DocumentEditor } from '@onlyoffice/document-editor-react';
import { ArrowLeft, History, Loader2, Pencil, Share2, Users } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/authContext';
import { colorForUserId, PresenceAvatars } from './components/PresenceAvatars';
import { PromptDialog } from './components/PromptDialog';
import { SaveStatus } from './components/SaveStatus';
import { ShareDialog } from './components/ShareDialog';
import { VersionHistoryPanel } from './components/VersionHistoryPanel';
import { useDocument, useRenameDocument } from './hooks/useDocuments';
import { useEditSession } from './hooks/useEditSession';
/**
 * EditorPage mounts the ONLYOFFICE collaborative editor, wrapped with a
 * Google-Docs-style header: presence avatars, save status, rename, share,
 * and version history.
 */ export default function EditorPage() {
    const { documentId } = useParams();
    const { user } = useAuth();
    const { data: doc, refetch: refetchDoc } = useDocument(documentId);
    const { data: session, error: sessionError, refetch: refetchSession } = useEditSession(documentId);
    const renameDoc = useRenameDocument();
    const [historyOpen, setHistoryOpen] = useState(false);
    const [renameOpen, setRenameOpen] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    // Bumped each time we need to remount <DocumentEditor> (e.g. after restore).
    const [editorEpoch, setEditorEpoch] = useState(0);
    const [presence, setPresence] = useState([]);
    const [saveState, setSaveState] = useState('idle');
    const [lastSavedAt, setLastSavedAt] = useState(undefined);
    const config = useMemo(()=>{
        if (!session) return null;
        try {
            return JSON.parse(session.editorConfigJson);
        } catch (err) {
            console.error('editor config parse failed', err);
            return null;
        }
    }, [
        session
    ]);
    const selfId = useMemo(()=>config?.editorConfig?.user?.id ?? user?.id ?? '', [
        config,
        user
    ]);
    const handleUsersEvent = useCallback((ev)=>{
        const raw = ev?.data;
        if (!Array.isArray(raw)) return;
        const next = raw.filter((u)=>u.id && u.id !== selfId).map((u)=>({
                id: u.id,
                name: u.name,
                color: colorForUserId(u.id)
            }));
        setPresence(next);
    }, [
        selfId
    ]);
    // ONLYOFFICE reports document state via onDocumentStateChange.
    //   data === true  → dirty (user has unsaved changes in their buffer)
    //   data === false → saved (autosave succeeded)
    // We also use onInfo to pick up transient saving events.
    const handleStateChange = useCallback((ev)=>{
        const data = ev?.data;
        if (data === true) {
            setSaveState('dirty');
        } else if (data === false) {
            setSaveState('saved');
            setLastSavedAt(new Date().toISOString());
        }
    }, []);
    const handleInfo = useCallback((ev)=>{
        const mode = ev?.data?.mode;
        if (mode === 'saving') setSaveState('saving');
    }, []);
    const handleRestored = useCallback(async ()=>{
        await refetchSession();
        await refetchDoc();
        setEditorEpoch((n)=>n + 1);
        setHistoryOpen(false);
    }, [
        refetchDoc,
        refetchSession
    ]);
    useEffect(()=>{
        if (sessionError) {
            toast.error(`Couldn't start edit session: ${sessionError.message}`);
        }
    }, [
        sessionError
    ]);
    if (sessionError) {
        return /*#__PURE__*/ _jsx("div", {
            className: "p-6",
            children: /*#__PURE__*/ _jsx("p", {
                className: "text-destructive",
                children: "Failed to load edit session."
            })
        });
    }
    if (!session || !config) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex items-center justify-center h-screen",
            children: /*#__PURE__*/ _jsx(Loader2, {
                className: "w-6 h-6 animate-spin text-muted-foreground"
            })
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col h-screen",
        children: [
            /*#__PURE__*/ _jsx(Helmet, {
                children: /*#__PURE__*/ _jsx("title", {
                    children: doc?.name ?? 'Document'
                })
            }),
            /*#__PURE__*/ _jsxs("header", {
                className: "flex items-center justify-between border-b px-4 py-2 bg-background gap-3",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-3 min-w-0",
                        children: [
                            /*#__PURE__*/ _jsxs(Link, {
                                to: "/editor",
                                className: "inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground",
                                children: [
                                    /*#__PURE__*/ _jsx(ArrowLeft, {
                                        className: "w-4 h-4"
                                    }),
                                    "All documents"
                                ]
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-muted-foreground",
                                children: "/"
                            }),
                            /*#__PURE__*/ _jsxs("button", {
                                type: "button",
                                onClick: ()=>setRenameOpen(true),
                                className: "group font-medium truncate inline-flex items-center gap-1 hover:text-foreground",
                                title: "Rename",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "truncate",
                                        children: doc?.name ?? '…'
                                    }),
                                    /*#__PURE__*/ _jsx(Pencil, {
                                        className: "w-3 h-3 opacity-0 group-hover:opacity-100 text-muted-foreground"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SaveStatus, {
                                state: saveState,
                                lastSavedAt: lastSavedAt
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-3",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-2",
                                title: "People editing now",
                                children: [
                                    /*#__PURE__*/ _jsx(Users, {
                                        className: "w-4 h-4 text-muted-foreground"
                                    }),
                                    /*#__PURE__*/ _jsx(PresenceAvatars, {
                                        users: presence
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("button", {
                                type: "button",
                                onClick: ()=>setHistoryOpen((v)=>!v),
                                className: "inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm hover:bg-muted",
                                children: [
                                    /*#__PURE__*/ _jsx(History, {
                                        className: "w-4 h-4"
                                    }),
                                    "Version history"
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("button", {
                                type: "button",
                                onClick: ()=>setShareOpen(true),
                                className: "inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:opacity-90",
                                children: [
                                    /*#__PURE__*/ _jsx(Share2, {
                                        className: "w-4 h-4"
                                    }),
                                    "Share"
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex-1 flex min-h-0",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex-1 min-w-0",
                        children: /*#__PURE__*/ _jsx(DocumentEditor, {
                            id: `editor-${documentId}-${editorEpoch}`,
                            documentServerUrl: session.documentServerUrl,
                            config: config,
                            events_onRequestUsers: handleUsersEvent,
                            events_onDocumentStateChange: handleStateChange,
                            events_onInfo: handleInfo,
                            events_onError: (ev)=>{
                                console.error('ONLYOFFICE error', ev);
                            }
                        })
                    }),
                    historyOpen && documentId && /*#__PURE__*/ _jsx("aside", {
                        className: "w-80 border-l overflow-y-auto",
                        children: /*#__PURE__*/ _jsx(VersionHistoryPanel, {
                            documentId: documentId,
                            onClose: ()=>setHistoryOpen(false),
                            onRestored: handleRestored
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(PromptDialog, {
                open: renameOpen,
                onOpenChange: setRenameOpen,
                title: "Rename document",
                label: "Document name",
                initialValue: doc?.name ?? '',
                confirmLabel: "Rename",
                onConfirm: async (name)=>{
                    if (!documentId) return;
                    await renameDoc.mutateAsync({
                        id: documentId,
                        name
                    });
                    await refetchDoc();
                    toast.success('Renamed');
                }
            }),
            documentId && /*#__PURE__*/ _jsx(ShareDialog, {
                open: shareOpen,
                onOpenChange: setShareOpen,
                documentId: documentId,
                documentName: doc?.name ?? 'Document'
            })
        ]
    });
}
